import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

import { SKIP_ACCEPT_LANGUAGE, SKIP_API_CACHE, SKIP_API_PREFIX, SKIP_ERROR_HANDLER, withSkips } from './http-context';

// HttpClient is declared in a re-exported module, so we have to extend the original module to make it work properly
// (see https://github.com/Microsoft/TypeScript/issues/13897)
declare module '@angular/common/http' {
    // Augment HttpClient with the added configuration methods so call sites keep
    // the exact `this.httpClient.skipErrorHandler().get(...)` ergonomics — the
    // injected HttpClient token is HttpService.
    export interface HttpClient {
        /** Skips the default error handler for this request. */
        skipErrorHandler(): HttpClient;
        /** Does not apply the API prefix for this request. */
        skipApiPrefix(): HttpClient;
        /** Does not set Accept-Language for this request. */
        skipAcceptLanguage(): HttpClient;
        /** Does not use the SSR response cache for this request. */
        skipCacheInterceptor(): HttpClient;
    }
}

/**
 * Drop-in `HttpClient` adding a per-request, fluent opt-out for the dynamic
 * interceptors / SSR cache: `httpClient.skipErrorHandler().get(...)`.
 *
 * Opt-outs are carried as `HttpContext` tokens ({@link withSkips}), so the
 * functional interceptors and `SsrCacheBackend` honour them — and the very same
 * mechanism works for a raw `HttpClient` call or `httpResource(...)`. Immutable:
 * each `.skip*()` returns a new instance reusing the shared `HttpHandler`; every
 * verb funnels through `request()`, so that single override injects the context
 * everywhere. No Angular internals, no per-call client construction, no bypass.
 */
class ConfigurableHttpClient extends HttpClient {
    constructor(
        private readonly httpHandler: HttpHandler,
        private readonly skips: ReadonlySet<HttpContextToken<boolean>>,
    ) {
        super(httpHandler);
    }

    override skipErrorHandler(): HttpClient {
        return this.skip(SKIP_ERROR_HANDLER);
    }

    override skipApiPrefix(): HttpClient {
        return this.skip(SKIP_API_PREFIX);
    }

    override skipAcceptLanguage(): HttpClient {
        return this.skip(SKIP_ACCEPT_LANGUAGE);
    }

    override skipCacheInterceptor(): HttpClient {
        return this.skip(SKIP_API_CACHE);
    }

    // get/post/put/delete/patch/head/options all delegate to request(), so this
    // single override is enough to attach the accumulated skip context.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    override request(method: any, url?: any, options: any = {}): Observable<any> {
        if (method instanceof HttpRequest) {
            return super.request(method.clone({ context: withSkips(this.skips, method.context) }));
        }

        return super.request(method, url, { ...options, context: withSkips(this.skips, <HttpContext | undefined>options.context) });
    }

    private skip(token: HttpContextToken<boolean>): ConfigurableHttpClient {
        return new ConfigurableHttpClient(this.httpHandler, new Set(this.skips).add(token));
    }
}

// No `providedIn`: the only registration is `{ provide: HttpClient, useClass: HttpService }`
// in app.config.ts. @Injectable() is still required (useClass target using inject()).
@Injectable()
export class HttpService extends ConfigurableHttpClient {
    constructor() {
        super(inject(HttpHandler), new Set<HttpContextToken<boolean>>());
    }
}
